import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Zap, FileText, Lock, Receipt } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";

/**
 * Activation modal for a single vehicle.
 *
 * Shows: vehicle summary, monthly cost impact (+ $X/mo or FREE if 2nd-vehicle-
 * per-tenant), and projected new monthly total. If the property is not on an
 * active subscription, replaces the activate CTA with a "Pay Setup Fee"
 * button that routes the user to /dashboard/billing.
 */
export default function ActivationModal({ vehicle, property, onClose, onActivated }) {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [summary, setSummary] = useState(null); // current billing summary
  const [unitActive, setUnitActive] = useState(null); // # active vehicles for this tenant's UNIT
  const perUnit = 1.6;

  // canActivate mirrors backend `_can_activate`: demo property OR setup fee paid.
  const canActivate = (() => {
    if (!property) return false;
    if (property.is_demo) return true;
    return !!property.setup_fee_paid_at;
  })();

  // Invoice-mode properties cannot activate per-vehicle; vehicles auto-activate
  // once the admin marks the issued monthly invoice as paid. Route the manager
  // to the Review page where the invoice can be issued.
  const isInvoiceMode = (property?.billing_mode || "").toLowerCase() === "invoice";

  const goToReview = () => {
    onClose();
    nav("/dashboard/onboard/review");
  };

  useEffect(() => {
    if (!property?.id || !vehicle) return;
    api.get(`/billing/summary?property_id=${property.id}`).then((r) => {
      setSummary(r.data);
    }).catch(() => setSummary(null));
    if (vehicle.tenant_id) {
      // Count active vehicles in the SAME UNIT (not just same tenant) — the
      // unit is the billing unit per the new pricing model.
      api.get(`/tenants/${vehicle.tenant_id}`).then(async (rt) => {
        const t = rt.data;
        const unit = (t.unit_number || "").trim();
        if (!unit) {
          setUnitActive(0);
          return;
        }
        try {
          const { data: tenants } = await api.get(
            `/tenants?property_id=${property.id}`
          );
          const sameUnitTenantIds = (tenants || [])
            .filter((x) => (x.unit_number || "").trim() === unit && (
              (x.building || "").trim() === (t.building || "").trim()
            ))
            .map((x) => x.id);
          let count = 0;
          for (const tid of sameUnitTenantIds) {
            const { data: vs } = await api.get(
              `/vehicles?property_id=${property.id}&tenant_id=${tid}&status=active`
            );
            count += (vs || []).filter((vv) => vv.id !== vehicle.id).length;
          }
          setUnitActive(count);
        } catch {
          setUnitActive(null);
        }
      }).catch(() => setUnitActive(null));
    }
  }, [property?.id, vehicle]);

  // v2 billing rule: every active car adds $1.00/mo (tier-1 entry rate).
  // Volume discounts and the free month are surfaced on Billing — for
  // a single-vehicle activation toggle we just add the per-car rate as
  // delta. `unitActive` is no longer used for the math but kept in
  // state so older `setUnitActive` calls don't blow up.
  const perCar = 1.0;
  const isFreeAdditionalInUnit = false;
  const delta = perCar;
  const currentMonthly = summary?.monthly_total ?? 0;
  const newMonthly = Math.round((currentMonthly + delta) * 100) / 100;
  void unitActive;
  void perUnit;

  const activate = async () => {
    setLoading(true);
    try {
      await api.post(`/vehicles/${vehicle.id}/activate`, {
        vehicle_id: vehicle.id,
        agreed: true,
      });
      toast.success("QR activated");
      onActivated && onActivated();
      onClose();
    } catch (e) {
      // 402 from backend = subscription required
      if (e?.response?.status === 402) {
        toast.error("Subscription required. Redirecting to Billing…");
        onClose();
        nav("/dashboard/billing");
        return;
      }
      // 403 from backend = invoice-mode lockdown ("Managers cannot
      // activate QR codes manually."). Don't dead-end — route the
      // manager to the Review page where they can issue the invoice
      // that auto-activates vehicles on payment.
      if (e?.response?.status === 403) {
        toast.message(
          "Submit your invoice to activate. Redirecting to the Review page…"
        );
        onClose();
        nav("/dashboard/onboard/review");
        return;
      }
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const goToBilling = async () => {
    // Take the user straight to Stripe — no intermediate Billing page.
    try {
      const { data } = await api.post("/billing/setup-fee", {
        property_id: property.id,
        origin_url: window.location.origin,
        redirect_after: "onboard",
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      // Fall back to the Billing page so the user can still reach the
      // checkout manually if the direct flow fails.
      onClose();
      nav("/dashboard/billing");
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="activation-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Activate this QR</DialogTitle>
          <DialogDescription>
            Confirm activation. Active cars are billed monthly at $1.00 each
            (tier-1). First 30 days after setup are free.
          </DialogDescription>
        </DialogHeader>

        {showAgreement ? (
          <div className="mt-2 bg-[#FAFAFA] border border-[#E4E4E7] rounded-md p-4 max-h-80 overflow-y-auto text-sm text-[#09090B] space-y-3">
            <div className="mono-tiny text-[#71717A]">Service agreement</div>
            <p>
              By activating a ScanMint QR sticker, you (the property manager) agree to pay
              <b> $1.00 per active car per month</b> prepaid monthly. Volume discounts apply at
              200 cars ($0.90/car), 500 cars ($0.85/car), and 1000+ cars require a custom quote.
              The first 30 days after the $99 setup fee is paid are free.
            </p>
            <p>
              ScanMint charges only active cars. Suspending a vehicle stops its billing at the
              next cycle. No long-term contracts. You may cancel anytime.
            </p>
            <p>
              Publicly-scannable pages never reveal tenant phone or email. Vehicle and status are
              displayed to facilitate verification only.
            </p>
          </div>
        ) : (
          <div className="mt-2 space-y-4">
            {/* Vehicle summary */}
            <div className="border border-[#E4E4E7] rounded-md p-4" data-testid="activation-vehicle-summary">
              <div className="mono-tiny text-[#71717A]">Vehicle</div>
              <div className="font-medium mt-1">
                {vehicle.year} {vehicle.make} {vehicle.model}
                {vehicle.color ? <span className="text-[#71717A]"> · {vehicle.color}</span> : null}
              </div>
              <div className="text-sm text-[#71717A] mt-0.5 font-mono">
                {vehicle.license_plate} · {vehicle.state}
              </div>
            </div>

            {/* Cost impact */}
            <div className="bg-[#0F8F4F]/5 border border-[#0F8F4F]/20 rounded-md p-5 space-y-3" data-testid="activation-cost-impact">
              <div className="flex items-baseline justify-between">
                <span className="mono-tiny text-[#0F8F4F]">Per-unit billing</span>
                <span className="text-xs text-[#71717A]">
                  ${perCar.toFixed(2)} / car / month
                </span>
              </div>
              <div className="font-display text-lg font-semibold" data-testid="activation-add-line">
                <>Activating this QR will add <span className="text-[#0F8F4F]">${delta.toFixed(2)}/mo</span></>
              </div>
              {summary && (
                <div className="text-sm text-[#3F3F46] flex items-baseline gap-2 pt-2 border-t border-[#0F8F4F]/10" data-testid="activation-totals-line">
                  <span>New monthly total:</span>
                  <b className="font-display text-base">${newMonthly.toFixed(2)}</b>
                  <span className="text-[#71717A]">(was ${currentMonthly.toFixed(2)})</span>
                </div>
              )}
              <p className="text-xs text-[#71717A]">
                Prepaid monthly. Cancel anytime. Volume discounts at 200/500 cars.
              </p>
            </div>
          </div>
        )}

        {!canActivate && (
          <div
            className="bg-[#FACC15]/10 border border-[#FACC15]/30 rounded-md p-4 mt-2 flex items-start gap-3"
            data-testid="activation-needs-subscription"
          >
            <Lock className="w-4 h-4 text-[#92400E] mt-0.5" strokeWidth={1.5} />
            <div className="text-sm text-[#92400E]">
              <b>Setup fee required.</b> Pay the $99 setup fee in Billing to activate QR codes for this property.
            </div>
          </div>
        )}

        {canActivate && isInvoiceMode && (
          <div
            className="bg-[#0F8F4F]/5 border border-[#0F8F4F]/30 rounded-md p-4 mt-2 flex items-start gap-3"
            data-testid="activation-invoice-mode-notice"
          >
            <Receipt className="w-4 h-4 text-[#0F8F4F] mt-0.5" strokeWidth={1.5} />
            <div className="text-sm text-[#3F3F46]">
              <b>Invoice mode.</b> This vehicle will activate automatically once
              you submit and pay your invoice. Continue to the <b>Review</b>{" "}
              page to issue it.
            </div>
          </div>
        )}

        <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
          {!showAgreement ? (
            <>
              <Button
                variant="outline"
                onClick={() => setShowAgreement(true)}
                data-testid="activation-view-agreement"
                className="h-11"
              >
                <FileText className="w-4 h-4 mr-2" /> View service agreement
              </Button>
              {!canActivate ? (
                <Button
                  onClick={goToBilling}
                  className="h-11 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
                  data-testid="activation-go-billing"
                >
                  <Lock className="w-4 h-4 mr-2" /> Pay your set up fee
                </Button>
              ) : isInvoiceMode ? (
                <Button
                  onClick={goToReview}
                  className="h-11 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
                  data-testid="activation-go-review"
                >
                  <Receipt className="w-4 h-4 mr-2" /> Add to invoice &amp; review
                </Button>
              ) : (
                <Button
                  onClick={activate}
                  disabled={loading}
                  className="h-11 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
                  data-testid="activation-agree"
                >
                  <Zap className="w-4 h-4 mr-2" /> Agree &amp; Activate
                </Button>
              )}
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setShowAgreement(false)}
                className="h-11"
                data-testid="activation-back"
              >
                Back
              </Button>
              {!canActivate ? (
                <Button
                  onClick={goToBilling}
                  className="h-11 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
                  data-testid="activation-go-billing-2"
                >
                  <Lock className="w-4 h-4 mr-2" /> Pay your set up fee
                </Button>
              ) : isInvoiceMode ? (
                <Button
                  onClick={goToReview}
                  className="h-11 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
                  data-testid="activation-go-review-2"
                >
                  <Receipt className="w-4 h-4 mr-2" /> Add to invoice &amp; review
                </Button>
              ) : (
                <Button
                  onClick={activate}
                  disabled={loading}
                  className="h-11 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
                  data-testid="activation-agree-from-agreement"
                >
                  <Zap className="w-4 h-4 mr-2" /> I agree &amp; Activate
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
